/**
 * useChatRoom — shared hook for loading and managing a chat room.
 *
 * Extracted from the legacy RoomPage `useRoomPage()` hook so both ChatPage
 * and any other consumer can reuse room data + Socket.IO event wiring without
 * duplicating the state machine.
 *
 * Responsibilities:
 *   - SWR fetch for room data + initial messages
 *   - Socket.IO lifecycle: join/leave, participant-ready, room-ready,
 *     messages, participant-state, room-closed
 *   - Optimistic message sending
 *   - Optimistic settings mutation (e.g. conversation style)
 *   - Reactions snapshot (C13): socket `message-reactions` plus REST on load and
 *     every (re)join, applied only when its revision is greater than the last applied
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import useSWR, { useSWRConfig } from 'swr'
import { toast } from 'sonner'

import { ChatImage, chatRoomsApi } from '@/lib/api'
import i18n from '@/i18n'
import { authenticatedChatRoomTransport, ChatRoomTransport, ChatRoomSendDocument } from './chatRoomTransport'
import { useTrialStatus } from '@/lib/hooks/use-trial-limits'
import { useAuth } from '@/lib/config/auth-provider'
import { getGuestSessionSubject } from '@/lib/utils/share-guest-intent'
import { SendOutcome } from '@/hooks/useTellerPicks'
import { chatQueueKey, loadChatQueue, saveChatQueue, removeAcceptedChatQueueItem, subscribeChatQueue, hasChatQueueStorageFailure, isWithheldRecoveryReason, type DeliveryRecoveryReason, type StoredChatQueueItem } from '@/lib/services/chat-queue-storage'
import { detectTrialLimitError, TRIAL_MAX_CHAT_MESSAGES_PER_ROOM } from '@/lib/billing/trial-limits'
import { isConversationHistoryKey, revalidateConversationLists } from '@/lib/chat/revalidateConversationLists'
import { patchSidebarRoomTitle, upsertSidebarRoom } from '@/components/layout/useSidebarThreads'
import {
  joinRoom,
  leaveRoom,
  onParticipantReady,
  onParticipantImage,
  onOpeningQuestion,
  onRoomReady,
  onRoomJoining,
  onRoomJoined,
  onMessage,
  onParticipantState,
  onTurnComplete,
  onRoomClosed,
  onTitleUpdated,
  onMessageReactions,
} from '@/lib/services/chat-socket'

import {
  ChatRoomDto,
  ChatRoomMessageDto,
  ChatRoomParticipantDto,
  ChatRoomParticipantState,
  ChatRoomReactionDto,
  ChatRoomReactionsEvent,
  ChatRoomSettings,
  ChatRoomStatus,
  GEORGE_PARTICIPANT_ID,
  RecentRoomItem,
} from '@digital-twins/shared-types'

import { getMessageBackfillDelayMs, shouldRetryMessageBackfill } from './messageBackfill'
import { orderRoomMessages } from './orderRoomMessages'
import {
  UNCONFIRMED_DELIVERY_ERROR,
  acceptedRequestIds,
  clientRequestIdOf,
  requestConfirmedByTwinReply,
} from './uncertainDelivery'

/**
 * `twins-answering`: a turn is in progress, the normal case after a successful send.
 * `room-busy`: the backend answered 409, so another writer holds the room lock.
 */
export type QueueBlockedReason = 'twins-answering' | 'room-busy'

export interface QueuedMessage extends StoredChatQueueItem {
  recoveryReason?: DeliveryRecoveryReason
  /** Persona tokens picked as tellers (C2); persisted so a retry resends the same picks. */
  tellerPicks?: Array<string>
  /** PDFs sent with the message (Step 10c). */
  documents?: Array<ChatRoomSendDocument>
}

function withoutRecoveryReason(item: QueuedMessage): QueuedMessage {
  const next = { ...item }
  delete next.recoveryReason
  return next
}

/** History may prove a generic uncertain send. Conflict and explicit recovery must keep their payload. */
function isGenericUncertainItem(item: QueuedMessage): boolean {
  return item.deliveryState === 'uncertain' && item.recoveryReason === 'generic' && Boolean(item.clientRequestId)
}

const RESTORED_QUEUE_NOTICE_PREFIX = 'Restored queued messages.'
const UNRECONCILED_RESTORED_SEND_ERROR =
  'A previous send could not be confirmed. Retry the queued message to check safely, or remove it.'

/** Clear a restored-send banner once every uncertain item has been accepted. */
function sendErrorAfterAcceptedRestore(previous: string | null, queue: Array<QueuedMessage>): string | null {
  if (previous === UNCONFIRMED_DELIVERY_ERROR) return null
  if (previous?.startsWith(RESTORED_QUEUE_NOTICE_PREFIX) || previous === UNRECONCILED_RESTORED_SEND_ERROR) {
    return queue.some(item => item.deliveryState !== 'queued') ? UNRECONCILED_RESTORED_SEND_ERROR : null
  }
  return previous
}

/** Extra send inputs from the composer (Step 11b picks, Step 11c PDFs). */
export interface HandleSendOptions {
  tellerPicks?: Array<string>
  documents?: Array<{ base64: string; name: string; mimeType: 'application/pdf' }>
}

/** Identity of one reaction for live-arrival animation: `${messageSequenceIndex}|${by}|${emoji}`. */
export function reactionKey(reaction: ChatRoomReactionDto): string {
  return `${reaction.messageSequenceIndex}|${reaction.by}|${reaction.emoji}`
}

/**
 * C13 revision gate: a snapshot applies only when its revision is greater than
 * the last applied one. Snapshots are full, so a stale one is dropped whole.
 */
export function shouldApplyReactionsSnapshot(
  lastAppliedRevision: number,
  snapshot: Pick<ChatRoomReactionsEvent, 'revision'>,
): boolean {
  return typeof snapshot.revision === 'number' && snapshot.revision > lastAppliedRevision
}

/**
 * Whether a participant's output for `turnId` already landed: its message
 * (the display message for the Research Assistant) or, for a reaction-only
 * twin, one of its reactions. A late `thinking` for such a participant is a
 * reordered event and must not bring the typing dots back.
 */
export function hasLandedForTurn(args: {
  turnId: string
  participantId: string
  messages: Array<ChatRoomMessageDto>
  reactions: Array<ChatRoomReactionDto>
  participants: Array<ChatRoomParticipantDto>
}): boolean {
  const { turnId, participantId, messages, reactions, participants } = args
  const isGeorge = participantId === GEORGE_PARTICIPANT_ID
  const hasMessage = messages.some((message) => {
    const metadata = message.metadata as Record<string, unknown> | null
    if (!metadata || metadata.responseTurnId !== turnId) {
      return false
    }
    if (isGeorge) {
      return message.senderType === 'george'
    }
    return message.senderType === 'twin' && message.senderParticipantId === participantId
  })
  if (hasMessage || isGeorge) {
    return hasMessage
  }
  const personaId = participants.find((participant) => participant.id === participantId)?.personaId
  return Boolean(personaId) && reactions.some((reaction) => reaction.turnId === turnId && reaction.by === personaId)
}

/**
 * Resolve which room token the hook should manage: an explicit `tokenOverride`
 * (inline embedding, e.g. a Quick Study focus group) ALWAYS wins; otherwise fall
 * back to the route token (`useParams`) for the standalone `/chats/:id` page.
 * Pure + exported so the embedding precedence is unit-tested without mounting the
 * hook (which pulls in SWR + Socket.IO). A blank override is treated as absent so a
 * stray empty string can't blank out a valid route token.
 *
 * Pass `enableRouteFallback = false` when calling from a non-chat route (e.g. Quick
 * Study) so the hook never picks up a non-UUID route param (like a QS session ID)
 * and accidentally fires a chat-room API request.
 */
export function resolveRoomToken(
  tokenOverride: string | undefined,
  routeToken: string | undefined,
  enableRouteFallback: boolean = true,
): string | undefined {
  const override = (tokenOverride ?? '').trim()
  return override || (enableRouteFallback ? routeToken : undefined)
}

/** Options of `handleSettingsChange`. */
export interface SettingsChangeOptions {
  /** Keys removed from the optimistic copy only; the server clears them on its side. */
  dropKeys?: Array<keyof ChatRoomSettings>
}

export interface OptimisticRenameArgs {
  /** The new title, already trimmed and non-empty. */
  title: string
  /** The title shown before the rename, restored on failure. */
  previous: string | null
  /**
   * The last title the server confirmed, read at failure time. Preferred over
   * `previous`, so two quick renames that both fail never restore an unsaved title.
   */
  getRollbackTitle?: () => string | null
  /** A functional setter, so a newer title (e.g. from the socket) is never clobbered. */
  setTitle: (update: (current: string | null) => string | null) => void
  save: (title: string) => Promise<{ title: string }>
  onFailure: () => void
  onSuccess: (saved: string) => void
}

/**
 * Optimistic rename: shows the new title at once, then saves it. On failure it
 * rolls back only while the shown title is still the optimistic one, so a
 * title that landed meanwhile stays. Returns whether the save succeeded.
 */
export async function runOptimisticRename(args: OptimisticRenameArgs): Promise<boolean> {
  const { title, previous, getRollbackTitle, setTitle, save, onFailure, onSuccess } = args
  setTitle(() => title)
  try {
    const result = await save(title)
    const saved = result?.title?.trim() || title
    setTitle((current) => (current === title ? saved : current))
    onSuccess(saved)
    return true
  } catch {
    const rollback = getRollbackTitle ? getRollbackTitle() : previous
    setTitle((current) => (current === title ? rollback : current))
    onFailure()
    return false
  }
}

/** The conversations list SWR keys (`['conversation-history', search]`). Lives with the other list keys. */
export { isConversationHistoryKey }

/** The sidebar rail row for a room the user just wrote in. */
function recentRoomItemFromRoom(
  room: ChatRoomDto,
  title: string | null,
  lastActivityAt: string,
): RecentRoomItem {
  const participantCount = room.participants?.length ?? 0
  return {
    kind: 'room',
    token: room.token,
    title: title?.trim() || null,
    roomType: participantCount === 1 ? 'one_on_one' : 'group',
    participantCount,
    firstParticipantName: room.participants?.[0]?.displayName ?? null,
    lastActivityAt,
  }
}

export interface UseChatRoomReturn {
  roomData: ChatRoomDto | undefined
  roomLoading: boolean
  roomError: unknown
  participants: Array<ChatRoomParticipantDto>
  roomStatus: ChatRoomStatus
  roomTitle: string | null
  messages: Array<ChatRoomMessageDto>
  messagesLoading: boolean
  participantStates: Record<string, ChatRoomParticipantState>
  thinkingParticipantIds: Array<string>
  isSending: boolean
  sendError: string | null
  isWaitingForTurn: boolean
  /**
   * Why the queue cannot drain right now, or null when it can. This is the SAME
   * value the drain effect gates on, exported so the UI never re-derives it: a
   * separately written visibility rule is how a queued message came to be hidden
   * during an ordinary turn while the drain was refusing to send it.
   */
  queueBlockedReason: QueueBlockedReason | null
  isClosed: boolean
  isPreparing: boolean
  isError: boolean
  /** True when the trial message cap is reached, replacing the composer with an upgrade notice. */
  atMessageLimit: boolean
  messageQueue: Array<QueuedMessage>
  blockedQueuedId: string | null
  handleQueueRetry: (id: string) => void
  /** Returns the queued item's clientRequestId, or undefined when the trial cap refused it. */
  handleSend: (
    content: string,
    images?: Array<ChatImage>,
    captureUrl?: string,
    stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>,
    options?: HandleSendOptions,
  ) => string | undefined
  /** clientRequestId of the latest send Nest accepted (2xx), or null. */
  lastAcceptedClientRequestId: string | null
  /**
   * Send outcomes this room session, in arrival order: accepted (2xx, or found in history),
   * failed (left the queue unsent), rekeyed (an edit gave the item a new clientRequestId).
   * A blocked or uncertain item records nothing: its picks stay pending under its id.
   */
  sendOutcomes: Array<SendOutcome>
  /** C13: the room's resolved reactions, from the last applied snapshot. */
  reactions: Array<ChatRoomReactionDto>
  /** Keys (`reactionKey`) of reactions that arrived live, so only they animate. */
  liveReactionKeys: ReadonlySet<string>
  handleQueueEdit: (id: string, content: string) => void
  handleQueueRemove: (id: string) => void
  /**
   * Optimistic settings PATCH, reverted on error. `options.dropKeys` leaves keys out of the
   * optimistic copy only (never sent): the server clears them itself, e.g. a level pick
   * removes `conversationModel` (composer model levels plan, D9).
   */
  handleSettingsChange: (
    partial: Partial<ChatRoomSettings>,
    options?: SettingsChangeOptions,
  ) => Promise<void>
  /**
   * Owner rename: optimistic, rolled back on failure only while the title is
   * still the optimistic one. Resolves to whether the save succeeded.
   */
  renameRoom: (title: string) => Promise<boolean>
  canUpdateSettings: boolean
  isSavingSettings: boolean
  settingsError: string | null
  /** Drops a shown settings error (the page clears it when the room pane toggles). */
  clearSettingsError: () => void
  navigate: ReturnType<typeof useNavigate>
  messagesError: boolean
  retryMessages: () => void
  /**
   * The room's suggested opening question, only while the room has no messages and
   * never for guests (shared links). Null otherwise.
   */
  openingQuestion: string | null
}

/**
 * Only an HTTP acknowledgement identifies which pending send was accepted.
 * Socket events and history reads may arrive out of order or belong to another
 * moderator. Carry decoded images only across an exact acknowledgement.
 */
export function mergeIncomingMessage(
  prev: Array<ChatRoomMessageDto>,
  incoming: ChatRoomMessageDto,
  acknowledgedOptimisticId?: string,
): Array<ChatRoomMessageDto> {
  if (incoming.senderType === 'user' && acknowledgedOptimisticId) {
    const idx = prev.findIndex((m) => m.id === acknowledgedOptimisticId && m.senderType === 'user')
    if (idx !== -1) {
      const carried = prev[idx].images
      const reconciled = carried && carried.length > 0 ? { ...incoming, images: carried } : incoming
      const next = prev.slice()
      const canonicalIndex = prev.findIndex((m) => m.id === incoming.id)
      if (canonicalIndex !== -1) {
        next[canonicalIndex] = reconciled
        return next.filter((_, index) => index !== idx)
      }
      next[idx] = reconciled
      return next.filter((m, index) => m.id !== incoming.id || index === idx)
    }
  }
  if (prev.some((m) => m.id === incoming.id)) return prev
  return [...prev, incoming]
}

function chatImagesMatch(
  left: Array<ChatImage> | undefined,
  right: Array<ChatImage> | undefined,
): boolean {
  if (!left?.length || !right?.length || left.length !== right.length) return false
  return left.every((image, index) => image.base64 === right[index].base64 && image.mimeType === right[index].mimeType)
}

/**
 * Canonical history precedes unacknowledged sends; the assistant's rows close
 * their turn (see `orderRoomMessages`); never mutate React state.
 */
function orderMessages(messages: Array<ChatRoomMessageDto>): Array<ChatRoomMessageDto> {
  return orderRoomMessages(messages)
}

/**
 * @param tokenOverride When provided, the hook manages THIS room token directly
 *   instead of reading it from the route (`useParams`). This is what lets a focus
 *   group be hosted INLINE inside the Quick Study page (which is not under the
 *   `/chats/:id` route). When omitted, behavior is identical to before — the
 *   standalone ChatPage passes nothing and the token comes from the route.
 * @param options.enableRouteFallback Set to `false` when calling from a page whose
 *   route param is NOT a chat-room token (e.g. `/quick-study/:id`). Prevents the
 *   hook from firing spurious API requests with a non-UUID route param.
 *   Defaults to `true` (preserves standalone ChatPage behaviour).
 */
export function useChatRoom(
  tokenOverride?: string,
  options?: {
    enableRouteFallback?: boolean
    /**
     * Swap the HTTP surface (guests use the public mirror). Defaults to the
     * authenticated `/chat-rooms/*` API.
     */
    transport?: ChatRoomTransport
  },
): UseChatRoomReturn {
  const { id: routeToken } = useParams<{ id: string }>()
  const enableRouteFallback = options?.enableRouteFallback ?? true
  const token = resolveRoomToken(tokenOverride, routeToken, enableRouteFallback)
  const navigate = useNavigate()
  const { mutate } = useSWRConfig()
  const transport = options?.transport ?? authenticatedChatRoomTransport
  const { user } = useAuth()
  const queueViewer = transport.scope.startsWith('guest:')
    ? getGuestSessionSubject(transport.scope.slice('guest:'.length))
    : user?.id ?? null
  const queueStorageKey = chatQueueKey(queueViewer, transport.scope, token)
  const queueStorageKeyRef = useRef(queueStorageKey)
  queueStorageKeyRef.current = queueStorageKey
  const isGuestTransport = transport.scope !== authenticatedChatRoomTransport.scope
  const canUpdateSettings = transport.canUpdateSettings
  // Guest rooms are owned — and paid for — by the study owner, so the viewer's
  // trial state is neither known nor relevant.
  const { isTrial: viewerIsTrial } = useTrialStatus()
  const isTrial = isGuestTransport ? false : viewerIsTrial

  const roomKey = token ? `chat-room-${transport.scope}-${token}` : null
  const roomKeyRef = useRef(roomKey)
  roomKeyRef.current = roomKey

  // Hoisted above every use: several callbacks below (the backfill effect,
  // `retryMessages`, the drain effect) read `tokenRef.current` from inside
  // closures that only run after this component function has finished its
  // synchronous body, so the TDZ never actually bites — but declaring it here,
  // before its first use, keeps that true by construction instead of by luck.
  const tokenRef = useRef(token)
  useEffect(() => {
    tokenRef.current = token
  }, [token])

  const {
    data: roomData,
    error: roomError,
    isLoading: roomLoading,
  } = useSWR<ChatRoomDto>(roomKey, () => transport.get(token!), {
    revalidateOnFocus: false,
  })

  const {
    data: initialMessages,
    error: initialMessagesError,
    isLoading: messagesLoading,
    isValidating: messagesValidating,
  } = useSWR(
    token ? `chat-room-messages-${transport.scope}-${token}` : null,
    () => transport.getMessages(token!),
    { revalidateOnFocus: false },
  )

  const [participants, setParticipants] = useState<Array<ChatRoomParticipantDto>>([])
  const [roomStatus, setRoomStatus] = useState<ChatRoomStatus>('preparing')
  const [roomTitle, setRoomTitle] = useState<string | null>(null)
  const [openingQuestion, setOpeningQuestion] = useState<string | null>(null)
  const roomTitleRef = useRef<string | null>(null)
  roomTitleRef.current = roomTitle
  // The last title the server confirmed (hydration, socket, a saved rename): the rename rollback target.
  const savedTitleRef = useRef<string | null>(null)
  const [messages, setMessages] = useState<Array<ChatRoomMessageDto>>([])
  const [participantStates, setParticipantStates] = useState<
    Record<string, ChatRoomParticipantState>
  >({})
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [isWaitingForTurn, setIsWaitingForTurn] = useState(false)
  const [turnInProgress, setTurnInProgress] = useState(false)
  const [messageQueue, setMessageQueue] = useState<Array<QueuedMessage>>([])
  const [queueRecoveryPending, setQueueRecoveryPending] = useState(false)
  const queueSnapshotReadyRef = useRef(false)
  const [queuePersistenceError, setQueuePersistenceError] = useState<string | null>(null)
  const queueStateRef = useRef<Array<QueuedMessage>>([])
  const roomSessionRef = useRef(0)
  // Session that may write the queue. Cleanup advances roomSessionRef and leaves
  // this behind, so an unmounted read cannot persist its stale snapshot.
  const queueWriteSessionRef = useRef(0)
  const canPersistQueue = () => queueWriteSessionRef.current !== 0
    && queueWriteSessionRef.current === roomSessionRef.current
  const persistQueue = useCallback((items: Array<QueuedMessage>, key = queueStorageKeyRef.current) => {
    if (!canPersistQueue()) return
    queueStateRef.current = items
    setMessageQueue(items)
    const saved = saveChatQueue(key, items)
    setQueuePersistenceError(items.length && !saved
      ? 'This browser could not save your queued messages. Keep this tab open to avoid losing them on reload.' : null)
  }, [])
  const [blockedQueuedId, setBlockedQueuedId] = useState<string | null>(null)
  const blockedQueuedIdRef = useRef<string | null>(null)
  const socketAcceptedRequestIdsRef = useRef(new Set<string>())
  const deliveryRecoveryGenerationRef = useRef(new Map<string, number>())
  const historyRereadIdsRef = useRef(new Set<string>())
  // Generic uncertain failures only. Conflict and send_recovery_required stay blocked
  // even when a reused clientRequestId is already in the transcript.
  const genericUncertainIdsRef = useRef(new Set<string>())
  const withheldRecoveryIdsRef = useRef(new Set<string>())
  const historyReadInFlightRef = useRef(new Set<string>())
  const historyReadDepthRef = useRef(new Map<string, { epoch: number, depth: number }>())
  const historyReadEpochRef = useRef(0)
  const initialHistoryTrackedRef = useRef<Array<string>>([])
  const twinSignalIdsRef = useRef(new Set<string>())
  // Payload of the HTTP call still in flight. History may remove it provisionally;
  // a later conflict or send_recovery_required puts this snapshot back.
  const dispatchedItemRef = useRef<QueuedMessage | null>(null)
  // Images copied onto a history row while this HTTP call is still unsettled.
  // A reused clientRequestId belongs to an earlier payload until the response says otherwise.
  const provisionalImageCarryRef = useRef<{
    messageId: string
    requestId: string
    images: Array<ChatImage>
  } | null>(null)
  const confirmAcceptedRequestRef = useRef<(item: QueuedMessage, fresh: Array<ChatRoomMessageDto>) => void>(() => {})
  const reconcileLiveDeliveryRef = useRef<(message: ChatRoomMessageDto) => void>(() => {})
  const consumeLatchedTwinsRef = useRef<(releasedIds: Array<string>, session: number) => Promise<void>>(async () => {})
  // A history read counts as in flight only for its room epoch. Overlapping
  // reads share a depth so one completion cannot unlatch the other.
  const beginHistoryRead = (ids: Array<string>): number => {
    const epoch = historyReadEpochRef.current
    for (const id of ids) {
      const current = historyReadDepthRef.current.get(id)
      const depth = current?.epoch === epoch ? current.depth + 1 : 1
      historyReadDepthRef.current.set(id, { epoch, depth })
      historyReadInFlightRef.current.add(id)
    }
    return epoch
  }
  const endHistoryRead = (ids: Array<string>, epoch: number): Array<string> => {
    if (epoch !== historyReadEpochRef.current) return []
    const released: Array<string> = []
    for (const id of ids) {
      const current = historyReadDepthRef.current.get(id)
      if (!current || current.epoch !== epoch) continue
      if (current.depth <= 1) {
        historyReadDepthRef.current.delete(id)
        historyReadInFlightRef.current.delete(id)
        released.push(id)
      } else {
        historyReadDepthRef.current.set(id, { epoch, depth: current.depth - 1 })
      }
    }
    return released
  }
  const clearHistoryRead = (id: string) => {
    historyReadDepthRef.current.delete(id)
    historyReadInFlightRef.current.delete(id)
  }
  const genericHistoryIds = (): Array<string> => queueStateRef.current
    .filter(item => isGenericUncertainItem(item)
      && genericUncertainIdsRef.current.has(item.id)
      && !withheldRecoveryIdsRef.current.has(item.id))
    .map(item => item.id)
  const [lastAcceptedClientRequestId, setLastAcceptedClientRequestId] = useState<string | null>(null)
  const [sendOutcomes, setSendOutcomes] = useState<Array<SendOutcome>>([])
  /** accepted: Nest took it; failed: the item left the queue unsent (remove, trial cap). A blocked item records nothing. */
  const recordSendOutcome = useCallback((clientRequestId: string | undefined, outcome: 'accepted' | 'failed') => {
    if (!clientRequestId) return
    setSendOutcomes(prev => [...prev, { clientRequestId, outcome }])
  }, [])
  const recoverQueueFromHistory = useCallback((fresh: Array<ChatRoomMessageDto>) => {
    const accepted = acceptedRequestIds(fresh)
    const historyCanAccept = (item: QueuedMessage) => isGenericUncertainItem(item) && accepted.has(item.clientRequestId!)
    const matched = queueStateRef.current.filter(historyCanAccept)
    for (const item of matched) confirmAcceptedRequestRef.current(item, fresh)
    const retained = queueStateRef.current.filter(item => !historyCanAccept(item))
    if (retained.length !== queueStateRef.current.length) persistQueue(retained)
    if (queueSnapshotReadyRef.current) {
      setQueueRecoveryPending(false)
      setSendError(previous => previous?.startsWith(RESTORED_QUEUE_NOTICE_PREFIX)
        ? retained.some(item => item.deliveryState !== 'queued')
          ? UNRECONCILED_RESTORED_SEND_ERROR : null
        : previous)
    }
  }, [persistQueue])
  consumeLatchedTwinsRef.current = async (releasedIds, session) => {
    const epochAtStart = historyReadEpochRef.current
    const stillLive = () => roomSessionRef.current === session && canPersistQueue() && historyReadEpochRef.current === epochAtStart
    if (!stillLive()) return
    let pending = releasedIds.filter(id => twinSignalIdsRef.current.has(id))
    let followUps = 0
    while (pending.length > 0 && followUps < 2) {
      if (!stillLive()) return
      for (const id of pending) twinSignalIdsRef.current.delete(id)
      const active = pending.filter(id => {
        const item = queueStateRef.current.find(entry => entry.id === id)
        return Boolean(
          item
          && isGenericUncertainItem(item)
          && genericUncertainIdsRef.current.has(id)
          && !withheldRecoveryIdsRef.current.has(id),
        )
      })
      if (active.length === 0) return
      const readToken = tokenRef.current
      if (!readToken) return
      const epoch = beginHistoryRead(active)
      let fresh: Array<ChatRoomMessageDto> | null = null
      try {
        fresh = await transportRef.current.getMessages(readToken)
      } catch {
        fresh = null
      }
      const released = endHistoryRead(active, epoch)
      followUps += 1
      if (!stillLive()) return
      if (fresh) recoverQueueFromHistory(fresh)
      pending = released.filter(id => twinSignalIdsRef.current.has(id))
    }
  }
  const [messagesError, setMessagesError] = useState(false)
  const [reactions, setReactions] = useState<Array<ChatRoomReactionDto>>([])
  const [liveReactionKeys, setLiveReactionKeys] = useState<ReadonlySet<string>>(() => new Set())
  const reactionsRevisionRef = useRef(-1)
  const reactionsRef = useRef<Array<ChatRoomReactionDto>>([])
  const participantsRef = useRef<Array<ChatRoomParticipantDto>>([])
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  // Set when the backend rejects a send with a trial message-limit 403, as a
  // correctness fallback in case the client-side count is stale.
  const [serverMessageLimitHit, setServerMessageLimitHit] = useState(false)
  const isDrainingRef = useRef(false)
  const retryCountRef = useRef(0)
  const retryItemIdRef = useRef<string | null>(null)
  // Effects and the drain loop deliberately avoid taking the transport as a
  // dependency (a guest transport is rebuilt per render) — read it via ref.
  const transportRef = useRef(transport)
  transportRef.current = transport
  const hasHydratedRef = useRef(false)
  const hasLoadedInitialMessages = useRef(false)
  const messagesRef = useRef<Array<ChatRoomMessageDto>>([])
  const settingsRequestRef = useRef(0)
  // The settings the server last confirmed (load, revalidate, socket, a saved PATCH): a
  // failed save rolls back to these. A pre-request snapshot can be an earlier request's
  // optimistic copy that the server never accepted.
  const confirmedSettingsRef = useRef<{ roomKey: string; settings: ChatRoomSettings } | null>(null)
  const pendingSettingsRequestsRef = useRef(new Set<number>())
  const sendRevisionRef = useRef(0)
  const activeTurnIdRef = useRef<string | null>(null)
  const awaitingTurnIdentityRef = useRef(false)
  const retiredTurnIdsRef = useRef(new Set<string>())
  const earlyCompletionsRef = useRef(new Set<string>())
  const latestUserSequenceRef = useRef(-1)

  const observeTurn = useCallback((turnId: string): boolean => {
    if (retiredTurnIdsRef.current.has(turnId)) return false
    if (awaitingTurnIdentityRef.current && activeTurnIdRef.current === turnId) return false
    if (activeTurnIdRef.current && activeTurnIdRef.current !== turnId) {
      retiredTurnIdsRef.current.add(activeTurnIdRef.current)
    }
    activeTurnIdRef.current = turnId
    awaitingTurnIdentityRef.current = false
    if (earlyCompletionsRef.current.delete(turnId)) {
      retiredTurnIdsRef.current.add(turnId)
      setTurnInProgress(false)
      setParticipantStates((prev) => Object.fromEntries(
        Object.entries(prev).map(([id, state]) => [id, state === 'thinking' ? 'idle' : state]),
      ))
      return false
    }
    return true
  }, [])

  const observeUserMessage = useCallback((message: ChatRoomMessageDto, acknowledged = false) => {
    if (message.senderType !== 'user' || (!acknowledged && message.sequenceIndex <= latestUserSequenceRef.current)) return
    latestUserSequenceRef.current = Math.max(latestUserSequenceRef.current, message.sequenceIndex)
    const turnId = message.metadata && 'turnId' in message.metadata ? message.metadata.turnId : undefined
    return typeof turnId === 'string' && observeTurn(turnId)
  }, [observeTurn])

  confirmAcceptedRequestRef.current = (item, fresh) => {
    if (!canPersistQueue()) return
    const current = queueStateRef.current.find(queued => queued.id === item.id)
    if (!current || current.clientRequestId !== item.clientRequestId || current.recoveryReason !== 'generic') return
    const requestId = current.clientRequestId
    const optimisticId = `optimistic-${current.id}`
    const canonical = requestId
      ? fresh.find(message => clientRequestIdOf(message) === requestId)
      : undefined
    const httpInFlight = dispatchedItemRef.current?.id === current.id
    if (httpInFlight && requestId && canonical && current.images?.length && !canonical.images?.length) {
      provisionalImageCarryRef.current = { messageId: canonical.id, requestId, images: current.images }
    }
    if (canonical) observeUserMessage(canonical, true)
    setMessages(prev => {
      let next = prev
      for (const message of fresh) {
        const isThisSend = Boolean(requestId) && clientRequestIdOf(message) === requestId
        const withImages = isThisSend && current.images?.length && !message.images?.length
          ? { ...message, images: current.images }
          : message
        next = mergeIncomingMessage(next, withImages, isThisSend ? optimisticId : undefined)
      }
      const confirmed = next.find(message => requestId && clientRequestIdOf(message) === requestId)
      if (confirmed) {
        const withImages = current.images?.length && !confirmed.images?.length
          ? { ...confirmed, images: current.images }
          : confirmed
        next = mergeIncomingMessage(next, withImages, optimisticId)
      }
      return orderMessages(next)
    })
    if (requestId) removeAcceptedChatQueueItem(queueStorageKeyRef.current, requestId)
    persistQueue(queueStateRef.current.filter(queued => queued.id !== current.id))
    if (requestId) {
      // Accepted though its ack was lost: its teller picks clear too.
      setLastAcceptedClientRequestId(requestId)
      recordSendOutcome(requestId, 'accepted')
    }
    deliveryRecoveryGenerationRef.current.delete(current.id)
    genericUncertainIdsRef.current.delete(current.id)
    withheldRecoveryIdsRef.current.delete(current.id)
    twinSignalIdsRef.current.delete(current.id)
    clearHistoryRead(current.id)
    if (requestId) socketAcceptedRequestIdsRef.current.delete(requestId)
    if (blockedQueuedIdRef.current === current.id) {
      blockedQueuedIdRef.current = null
      setBlockedQueuedId(null)
    }
    setSendError(previous => sendErrorAfterAcceptedRestore(previous, queueStateRef.current))
    setIsSending(false)
  }

  const beginHistoryReread = (item: QueuedMessage, generation: number) => {
    const requestId = item.clientRequestId
    const readToken = tokenRef.current
    const session = roomSessionRef.current
    if (!requestId || !readToken || historyRereadIdsRef.current.has(item.id)) return
    const queued = queueStateRef.current.find(entry => entry.id === item.id)
    if (!queued || !isGenericUncertainItem(queued)) return
    if (!genericUncertainIdsRef.current.has(item.id) || withheldRecoveryIdsRef.current.has(item.id)) return
    historyRereadIdsRef.current.add(item.id)
    void (async () => {
      let followUps = 0
      while (followUps < 2) {
        if (roomSessionRef.current !== session) return
        const epoch = beginHistoryRead([item.id])
        let fresh: Array<ChatRoomMessageDto> | null = null
        try {
          fresh = await transportRef.current.getMessages(readToken)
        } catch {
          fresh = null
        } finally {
          endHistoryRead([item.id], epoch)
        }
        if (roomSessionRef.current !== session || !canPersistQueue()) return
        if (deliveryRecoveryGenerationRef.current.get(item.id) !== generation) return
        if (withheldRecoveryIdsRef.current.has(item.id) || !genericUncertainIdsRef.current.has(item.id)) return
        const current = queueStateRef.current.find(queued => queued.id === item.id && queued.clientRequestId === requestId && isGenericUncertainItem(queued))
        if (current && fresh && acceptedRequestIds(fresh).has(requestId)) {
          twinSignalIdsRef.current.delete(item.id)
          confirmAcceptedRequestRef.current(current, fresh)
          return
        }
        if (!twinSignalIdsRef.current.has(item.id)) return
        twinSignalIdsRef.current.delete(item.id)
        followUps += 1
      }
    })().finally(() => {
      if (roomSessionRef.current === session) historyRereadIdsRef.current.delete(item.id)
    })
  }

  reconcileLiveDeliveryRef.current = (message) => {
    const directId = clientRequestIdOf(message)
    if (directId) {
      const matched = queueStateRef.current.find(item => item.clientRequestId === directId)
      if (matched && isGenericUncertainItem(matched) && !withheldRecoveryIdsRef.current.has(matched.id)) {
        socketAcceptedRequestIdsRef.current.add(directId)
      }
    }
    for (const item of queueStateRef.current) {
      if (!item.clientRequestId || !isGenericUncertainItem(item) || !genericUncertainIdsRef.current.has(item.id)) continue
      if (withheldRecoveryIdsRef.current.has(item.id)) continue
      const matchedUser = directId === item.clientRequestId
      const matchedTwin = message.senderType === 'twin'
        && requestConfirmedByTwinReply(messagesRef.current, item.clientRequestId, message)
      if (!matchedUser && !matchedTwin) continue
      confirmAcceptedRequestRef.current(item, matchedUser ? [message] : messagesRef.current)
    }
    if (message.senderType !== 'twin') return
    for (const item of queueStateRef.current) {
      if (!item.clientRequestId || !isGenericUncertainItem(item) || !genericUncertainIdsRef.current.has(item.id)) continue
      if (withheldRecoveryIdsRef.current.has(item.id)) continue
      if (acceptedRequestIds(messagesRef.current).has(item.clientRequestId)) continue
      if (historyReadInFlightRef.current.has(item.id)) {
        twinSignalIdsRef.current.add(item.id)
        continue
      }
      const generation = deliveryRecoveryGenerationRef.current.get(item.id)
      if (generation == null) continue
      beginHistoryReread(item, generation)
    }
  }
  // Accepted sends from older servers lack an exact-message ACK. Keep their
  // identities until a successful canonical history read reconciles them.
  const acceptedLegacyIdsRef = useRef(new Set<string>())

  useEffect(() => {
    roomSessionRef.current += 1
    queueWriteSessionRef.current = roomSessionRef.current
    const restored = loadChatQueue(queueStorageKey)
    activeTurnIdRef.current = null
    awaitingTurnIdentityRef.current = false
    retiredTurnIdsRef.current.clear()
    earlyCompletionsRef.current.clear()
    latestUserSequenceRef.current = -1
    acceptedLegacyIdsRef.current.clear()
    hasHydratedRef.current = false
    hasLoadedInitialMessages.current = false
    isDrainingRef.current = false
    retryCountRef.current = 0
    retryItemIdRef.current = null
    setParticipants([])
    setMessages([])
    reactionsRevisionRef.current = -1
    reactionsRef.current = []
    setReactions([])
    setLiveReactionKeys(new Set())
    setLastAcceptedClientRequestId(null)
    setSendOutcomes([])
    setMessagesError(false)
    setQueuePersistenceError(restored.length && hasChatQueueStorageFailure(queueStorageKey)
      ? 'This browser could not save your queued messages. Keep this tab open to avoid losing them on reload.' : null)
    setRoomStatus('preparing')
    setRoomTitle(null)
    setOpeningQuestion(null)
    savedTitleRef.current = null
    setParticipantStates({})
    setIsSending(false)
    setIsWaitingForTurn(false)
    setTurnInProgress(false)
    queueSnapshotReadyRef.current = false
    queueStateRef.current = restored
    setMessageQueue(restored)
    setQueueRecoveryPending(restored.length > 0)
    socketAcceptedRequestIdsRef.current.clear()
    deliveryRecoveryGenerationRef.current.clear()
    historyRereadIdsRef.current.clear()
    genericUncertainIdsRef.current.clear()
    withheldRecoveryIdsRef.current.clear()
    historyReadEpochRef.current += 1
    historyReadInFlightRef.current.clear()
    historyReadDepthRef.current.clear()
    initialHistoryTrackedRef.current = []
    twinSignalIdsRef.current.clear()
    dispatchedItemRef.current = null
    provisionalImageCarryRef.current = null
    for (const item of restored) {
      if (isWithheldRecoveryReason(item.recoveryReason)) {
        withheldRecoveryIdsRef.current.add(item.id)
        continue
      }
      if (!isGenericUncertainItem(item)) continue
      genericUncertainIdsRef.current.add(item.id)
      deliveryRecoveryGenerationRef.current.set(item.id, 1)
    }
    const pendingInitial = genericHistoryIds()
    if (pendingInitial.length > 0 && tokenRef.current) {
      initialHistoryTrackedRef.current = pendingInitial
      beginHistoryRead(pendingInitial)
    }
    blockedQueuedIdRef.current = restored.find(item => item.deliveryState && item.deliveryState !== 'queued')?.id ?? null
    setBlockedQueuedId(blockedQueuedIdRef.current)
    setSendError(null)
    if (restored.length) setSendError(`${RESTORED_QUEUE_NOTICE_PREFIX} Waiting to reconnect; unconfirmed sends need your confirmation to retry.`)
    setMessagesError(false)
    setSettingsError(null)
    setIsSavingSettings(false)
    pendingSettingsRequestsRef.current.clear()
    setServerMessageLimitHit(false)
    const historyReads = historyReadInFlightRef.current
    const historyDepth = historyReadDepthRef.current
    const twinSignals = twinSignalIdsRef.current
    const historyRereads = historyRereadIdsRef.current
    return () => {
      roomSessionRef.current += 1
      historyReadEpochRef.current += 1
      historyReads.clear()
      historyDepth.clear()
      initialHistoryTrackedRef.current = []
      twinSignals.clear()
      historyRereads.clear()
    }
  }, [roomKey, queueStorageKey])

  useEffect(() => subscribeChatQueue(queueStorageKey, () => {
    const stored = loadChatQueue(queueStorageKey)
    queueStateRef.current = stored
    setMessageQueue(stored)
    const nextBlocked = stored.some(item => item.id === blockedQueuedIdRef.current)
      ? blockedQueuedIdRef.current
      : null
    blockedQueuedIdRef.current = nextBlocked
    setBlockedQueuedId(nextBlocked)
  }), [queueStorageKey])

  useEffect(() => {
    if (roomData && !hasHydratedRef.current) {
      setParticipants(roomData.participants)
      setRoomStatus(roomData.status)
      setRoomTitle(roomData.title)
      setOpeningQuestion(roomData.openingQuestion?.trim() || null)
      savedTitleRef.current = roomData.title
      hasHydratedRef.current = true
      return
    }
    if (!roomData) {
      return
    }
    // A revalidated room backfills what a missed socket event would have set: the
    // opening question, only where local state is still empty.
    const question = roomData.openingQuestion?.trim()
    if (question && messagesRef.current.length === 0) {
      setOpeningQuestion((prev) => prev ?? question)
    }
  }, [roomData])

  useEffect(() => {
    const session = roomSessionRef.current
    if (initialMessages && !hasLoadedInitialMessages.current) {
      for (const message of initialMessages) {
        if (message.senderType === 'user') latestUserSequenceRef.current = Math.max(latestUserSequenceRef.current, message.sequenceIndex)
      }
      setMessages((prev) =>
        orderMessages(initialMessages.reduce((next, message) => mergeIncomingMessage(next, message), prev)),
      )
      hasLoadedInitialMessages.current = true
    }
    // isLoading is false for cached data while isValidating is still true.
    // Hydration above is one-time; reconciliation follows each settled fetch.
    const validating = Boolean(messagesLoading) || Boolean(messagesValidating)
    if (validating) {
      const tracked = new Set(initialHistoryTrackedRef.current)
      const pending = genericHistoryIds().filter(id => !tracked.has(id))
      if (pending.length > 0) {
        initialHistoryTrackedRef.current = [...initialHistoryTrackedRef.current, ...pending]
        beginHistoryRead(pending)
      }
      return
    }
    if (roomSessionRef.current !== session) return
    if (initialMessages) recoverQueueFromHistory(initialMessages)
    const tracked = initialHistoryTrackedRef.current
    if (tracked.length === 0) return
    initialHistoryTrackedRef.current = []
    const released = endHistoryRead(tracked, historyReadEpochRef.current)
    void consumeLatchedTwinsRef.current(released, session)
  }, [initialMessages, initialMessagesError, messagesLoading, messagesValidating, roomKey, recoverQueueFromHistory])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    participantsRef.current = participants
  }, [participants])

  useEffect(() => {
    if (!roomData?.token) return

    const roomToken = roomData.token
    const session = roomSessionRef.current
    let backfillTimer: ReturnType<typeof setTimeout> | null = null
    let cancelled = false
    let joinSendRevision = sendRevisionRef.current
    let liveStateRevision = 0
    let joinStateRevision = 0
    let completionRevision = 0
    let userTurnStartRevision = 0
    const participantStateRevisions = new Map<string, number>()
    const latestLiveParticipantStates = new Map<string, ChatRoomParticipantState>()
    // Rev 3: `${turnId}|${participantId}` of every final idle (Nest marks the idle after the twin's
    // own callback). A silent reactor lands no message or reaction, so this is what keeps a reordered
    // late `thinking` from bringing its dots back. A cast's wave-1 idle is not final: wave 2 still thinks.
    const idledForTurn = new Set<string>()
    let recoveryRevision = 0
    const isCurrentRoom = () => !cancelled && roomSessionRef.current === session

    // C13: one gate for socket and REST snapshots. Only live snapshots mark
    // their new reactions for the pop; load and reconnect reads never animate.
    const applyReactions = (snapshot: ChatRoomReactionsEvent, isLive: boolean) => {
      if (!isCurrentRoom() || !Array.isArray(snapshot.reactions)) return
      if (!shouldApplyReactionsSnapshot(reactionsRevisionRef.current, snapshot)) return
      reactionsRevisionRef.current = snapshot.revision
      const previousKeys = new Set(reactionsRef.current.map(reactionKey))
      reactionsRef.current = snapshot.reactions
      setReactions(snapshot.reactions)
      setLiveReactionKeys(isLive
        ? new Set(snapshot.reactions.map(reactionKey).filter((key) => !previousKeys.has(key)))
        : new Set())
    }
    const loadReactions = async () => {
      const getReactions = transportRef.current.getReactions
      if (!getReactions) return
      try {
        applyReactions(await getReactions(roomToken), false)
      } catch {
        // Chips are additive: a failed read leaves the last applied snapshot.
      }
    }
    void loadReactions()

    const offJoining = onRoomJoining((joiningRoom) => {
      if (joiningRoom === roomToken && isCurrentRoom()) {
        joinSendRevision = sendRevisionRef.current
        joinStateRevision = liveStateRevision
      }
    })
    const offJoined = onRoomJoined(async (event) => {
      if (event.roomId !== roomToken || !isCurrentRoom()) return
      // An empty room re-reads itself once joined: the opening question
      // emitted between the REST read and the join are backfilled from roomData.
      if (messagesRef.current.length === 0 && roomKeyRef.current) {
        void mutate(roomKeyRef.current)
      }
      if (event.thinkingParticipantIds) queueSnapshotReadyRef.current = true
      const revision = ++recoveryRevision
      // Apply the snapshot before awaiting history: later socket events must
      // win, including a turn that finishes while the HTTP read is in flight.
      // A join requested before a local send can contain an older idle state.
      const snapshotJoinRevision = joinStateRevision
      const noNewLiveState = snapshotJoinRevision === liveStateRevision
      const canReconcileSnapshot = joinSendRevision === sendRevisionRef.current &&
        completionRevision <= snapshotJoinRevision && !isDrainingRef.current &&
        (!event.turnId || noNewLiveState || event.turnId === activeTurnIdRef.current)
      if (event.thinkingParticipantIds && canReconcileSnapshot &&
        (!event.turnId || observeTurn(event.turnId))) {
        const thinking = new Set(event.thinkingParticipantIds)
        setParticipantStates((prev) => {
          const next = { ...prev }
          for (const id of Object.keys(next)) {
            if ((participantStateRevisions.get(id) ?? 0) > snapshotJoinRevision) continue
            if (next[id] === 'thinking' && !thinking.has(id)) next[id] = 'idle'
          }
          for (const id of thinking) {
            if ((participantStateRevisions.get(id) ?? 0) <= snapshotJoinRevision) next[id] = 'thinking'
          }
          return next
        })
        // New live thinking must keep the lock, but a live idle event must not
        // prevent an authoritative idle snapshot recovering a missed completion.
        const effectiveThinking = new Set(thinking)
        for (const [id, state] of latestLiveParticipantStates) {
          if ((participantStateRevisions.get(id) ?? 0) <= snapshotJoinRevision) continue
          if (state === 'idle') effectiveThinking.delete(id)
          else effectiveThinking.add(id)
        }
        setTurnInProgress(Boolean(event.turnId) || effectiveThinking.size > 0 ||
          userTurnStartRevision > snapshotJoinRevision)
      }
      const currentTransport = transportRef.current
      const trackedIds = genericHistoryIds()
      const historyEpoch = beginHistoryRead(trackedIds)
      let historyReadEnded = false
      const finishHistoryRead = () => {
        if (historyReadEnded) return [] as Array<string>
        historyReadEnded = true
        return endHistoryRead(trackedIds, historyEpoch)
      }
      try {
        const fresh = await currentTransport.getMessages(roomToken)
        const released = finishHistoryRead()
        if (!isCurrentRoom()) return
        if (recoveryRevision !== revision) {
          void consumeLatchedTwinsRef.current(released, session)
          return
        }
        recoverQueueFromHistory(fresh)
        for (const message of fresh) {
          if (message.senderType === 'user') latestUserSequenceRef.current = Math.max(latestUserSequenceRef.current, message.sequenceIndex)
        }
        setMessages((prev) => orderMessages(fresh.reduce(
          (next, message) => mergeIncomingMessage(next, message), prev,
        )))
        setMessagesError(false)
        void consumeLatchedTwinsRef.current(released, session)
      } catch {
        const released = finishHistoryRead()
        if (isCurrentRoom()) void consumeLatchedTwinsRef.current(released, session)
        if (isCurrentRoom() && recoveryRevision === revision) setMessagesError(true)
      }
      // Reconnect: the REST snapshot covers anything the socket missed.
      void loadReactions()
    })

    const offReactions = onMessageReactions((event) => {
      if (event.roomId !== roomToken) return
      applyReactions(event, true)
    })

    const scheduleBackfill = (attempt: number) => {
      const delayMs = getMessageBackfillDelayMs(attempt)
      if (delayMs == null) return
      if (backfillTimer) clearTimeout(backfillTimer)
      backfillTimer = setTimeout(() => {
        void attemptBackfill(attempt)
      }, delayMs)
    }

    const attemptBackfill = async (attempt: number) => {
      if (cancelled || roomSessionRef.current !== session) return
      if (messagesRef.current.length > 0) return
      if (!hasLoadedInitialMessages.current) {
        // Initial SWR hydrate has not settled yet — retry the same attempt index
        // so we do not burn the empty-response budget on a not-ready client.
        scheduleBackfill(attempt)
        return
      }

      const token = tokenRef.current
      if (!token) return

      try {
        const fresh = await transportRef.current.getMessages(token)
        if (cancelled || roomSessionRef.current !== session) return
        if (messagesRef.current.length > 0) return

        if (fresh.length === 0) {
          if (
            shouldRetryMessageBackfill({
              messagesEmpty: true,
              hasLoadedInitialMessages: true,
              attemptJustCompleted: attempt,
            })
          ) {
            scheduleBackfill(attempt + 1)
          }
          return
        }

        setMessagesError(false)
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id))
          const missing = fresh.filter((m) => !existingIds.has(m.id))
          if (missing.length === 0) return prev
          let next = prev
          for (const m of missing) next = mergeIncomingMessage(next, m)
          return orderMessages(next)
        })
      } catch {
        if (cancelled || roomSessionRef.current !== session) return
        if (
          shouldRetryMessageBackfill({
            messagesEmpty: messagesRef.current.length === 0,
            hasLoadedInitialMessages: true,
            attemptJustCompleted: attempt,
          })
        ) {
          scheduleBackfill(attempt + 1)
        } else if (messagesRef.current.length === 0) {
          setMessagesError(true)
        }
      }
    }

    // Active rooms can race ahead of intro callbacks. Keep retrying empty
    // REST reads for a few seconds so late-joining clients still hydrate.
    if (roomData.status !== 'preparing') {
      scheduleBackfill(0)
    }

    const offParticipantReady = onParticipantReady((event) => {
      if (event.roomId !== roomToken) return
      setParticipants((prev) =>
        prev.map((p) =>
          p.id === event.participantId
            ? {
                ...p,
                enrichmentStatus: 'ready' as const,
                imageUrl: event.imageUrl,
                displayName: event.displayName,
                featuredTwinId: event.featuredTwinId,
                ...(event.bio != null ? { bio: event.bio } : {}),
                ...(event.tagline != null ? { tagline: event.tagline } : {}),
                ...(event.audienceTagNames ? { audienceTagNames: event.audienceTagNames } : {}),
              }
            : p,
        ),
      )
    })

    const offParticipantImage = onParticipantImage((event) => {
      if (event.roomId !== roomToken) return
      setParticipants((prev) =>
        prev.map((p) => (p.id === event.participantId ? { ...p, imageUrl: event.imageUrl } : p)),
      )
    })

    // The suggestion is for an empty room only; one landing after the first message is dropped.
    const offOpeningQuestion = onOpeningQuestion((event) => {
      if (event.roomId !== roomToken) {
        return
      }
      if (messagesRef.current.length > 0) {
        return
      }
      const question = event.question?.trim()
      if (question) {
        setOpeningQuestion(question)
      }
    })

    const offRoomReady = onRoomReady((event) => {
      if (event.roomId !== roomToken) return
      setRoomStatus('active')
      // Intros fire right after activation — kick (or re-kick) REST backfill in
      // case the client missed the socket burst that follows room-ready.
      if (messagesRef.current.length === 0) {
        scheduleBackfill(0)
      }
    })

    const offMessage = onMessage((event) => {
      if (event.roomId !== roomToken || !isCurrentRoom()) return
      if (observeUserMessage(event.message)) {
        liveStateRevision += 1
        userTurnStartRevision = liveStateRevision
        setTurnInProgress(true)
      }
      if (event.message.senderType !== 'user') {
        // Synchronous record, so a reordered `thinking` in the same tick sees it.
        messagesRef.current = mergeIncomingMessage(messagesRef.current, event.message)
      }
      setMessages((prev) => orderMessages(mergeIncomingMessage(prev, event.message)))
      reconcileLiveDeliveryRef.current(event.message)
    })

    const offParticipantState = onParticipantState((event) => {
      if (event.roomId !== roomToken || !isCurrentRoom()) return
      if (event.turnId) {
        const turnKey = `${event.turnId}|${event.participantId}`
        if (event.state === 'idle' && event.final === true) {
          idledForTurn.add(turnKey)
        } else if (event.state === 'thinking' && idledForTurn.has(turnKey)) {
          return
        }
        if (event.state === 'thinking') {
          // Step 10 review: a thinking for a participant whose output for this
          // turn already landed arrived out of order; the dots stay off.
          if (hasLandedForTurn({
            turnId: event.turnId,
            participantId: event.participantId,
            messages: messagesRef.current,
            reactions: reactionsRef.current,
            participants: participantsRef.current,
          })) return
          if (!observeTurn(event.turnId)) return
          setTurnInProgress(true)
        } else if (awaitingTurnIdentityRef.current || retiredTurnIdsRef.current.has(event.turnId) ||
          (activeTurnIdRef.current && activeTurnIdRef.current !== event.turnId)) return
      }
      liveStateRevision += 1
      participantStateRevisions.set(event.participantId, liveStateRevision)
      latestLiveParticipantStates.set(event.participantId, event.state)
      setParticipantStates((prev) => ({
        ...prev,
        [event.participantId]: event.state,
      }))
    })

    const offTurnComplete = onTurnComplete((event) => {
      if (event.roomId !== roomToken || !isCurrentRoom()) return
      if (event.turnId) {
        if (retiredTurnIdsRef.current.has(event.turnId)) return
        if (awaitingTurnIdentityRef.current) {
          if (event.turnId !== activeTurnIdRef.current) earlyCompletionsRef.current.add(event.turnId)
          return
        }
        if (activeTurnIdRef.current && activeTurnIdRef.current !== event.turnId) return
        retiredTurnIdsRef.current.add(event.turnId)
      }
      liveStateRevision += 1
      completionRevision = liveStateRevision
      setTurnInProgress(false)
      setParticipantStates((prev) => Object.fromEntries(
        Object.entries(prev).map(([id, state]) => [id, state === 'thinking' ? 'idle' : state]),
      ))
    })

    const offClosed = onRoomClosed((event) => {
      if (event.roomId !== roomToken) return
      setRoomStatus('closed')
      if (event.title) {
        savedTitleRef.current = event.title
        setRoomTitle(event.title)
      }
      // The /chats status column; guests own none of the conversation lists.
      if (transportRef.current.scope === authenticatedChatRoomTransport.scope) {
        revalidateConversationLists(mutate)
      }
    })

    // RND-2580: server (re)generated the conversation title — reflect it live.
    const offTitleUpdated = onTitleUpdated((event) => {
      if (event.roomId !== roomToken) return
      if (event.title) {
        savedTitleRef.current = event.title
        setRoomTitle(event.title)
        // Guests own none of the conversation lists.
        if (transportRef.current.scope === authenticatedChatRoomTransport.scope) {
          patchSidebarRoomTitle(roomToken, event.title)
          revalidateConversationLists(mutate, { includeRail: false })
        }
      }
    })

    // Register all listeners before joining, including synchronous test and
    // fast local-server acknowledgements.
    joinRoom(roomToken)

    return () => {
      cancelled = true
      if (backfillTimer) clearTimeout(backfillTimer)
      leaveRoom(roomToken)
      offParticipantReady()
      offParticipantImage()
      offOpeningQuestion()
      offRoomReady()
      offMessage()
      offParticipantState()
      offTurnComplete()
      offClosed()
      offTitleUpdated()
      offJoining()
      offJoined()
      offReactions()
    }
  }, [roomKey, queueStorageKey, roomData?.token, roomData?.status, observeTurn, observeUserMessage, recoverQueueFromHistory, mutate])

  const isClosed = roomStatus === 'closed'
  const isPreparing = roomStatus === 'preparing'
  const isError = roomStatus === 'error'

  // Count the user's own confirmed messages plus any queued-but-not-yet-sent
  // messages. Optimistic placeholders (prefixed `optimistic-`) are excluded
  // because they aren't committed yet.
  const userMessageCount = useMemo(
    () =>
      messages.filter((m) => m.senderType === 'user' && !m.id.startsWith('optimistic-')).length +
      messageQueue.length,
    [messages, messageQueue],
  )

  // Use `>` (not `>=`) so the composer stays visible when there are exactly
  // TRIAL_MAX_CHAT_MESSAGES_PER_ROOM confirmed messages — the user can still
  // type and click send. The count only exceeds the cap once the 6th message is
  // added to the queue (inside handleSend), flipping atMessageLimit to true
  // on the next render immediately after the click. The backend 403 is a
  // correctness fallback (serverMessageLimitHit) for e.g. stale client counts.
  const atMessageLimit =
    serverMessageLimitHit || (isTrial && userMessageCount > TRIAL_MAX_CHAT_MESSAGES_PER_ROOM)

  // Ref so the memoized handleSend can read the latest cap state
  // without being re-created on every message.
  const atMessageLimitRef = useRef(atMessageLimit)
  useEffect(() => {
    atMessageLimitRef.current = atMessageLimit
  }, [atMessageLimit])

  const thinkingParticipantIds = useMemo(
    () =>
      Object.entries(participantStates)
        .filter(([, state]) => state === 'thinking')
        .map(([id]) => id),
    [participantStates],
  )

  // Server copies only: skipped while a save is in flight (roomData is then optimistic).
  useEffect(() => {
    if (!roomData || !roomKey || pendingSettingsRequestsRef.current.size > 0) return
    confirmedSettingsRef.current = { roomKey, settings: roomData.settings }
  }, [roomData, roomKey])

  const handleSettingsChange = useCallback(
    async (partial: Partial<ChatRoomSettings>, options?: SettingsChangeOptions) => {
      if (!canUpdateSettings || !token || !roomData || !roomKey) return
      const session = roomSessionRef.current
      const requestId = ++settingsRequestRef.current
      const isSameRoom = () => roomSessionRef.current === session
      const isCurrent = () => isSameRoom() && settingsRequestRef.current === requestId

      const previous = roomData
      const optimisticSettings: ChatRoomSettings = { ...roomData.settings, ...partial }
      for (const key of options?.dropKeys ?? []) {
        delete optimisticSettings[key]
      }
      const optimistic: ChatRoomDto = {
        ...roomData,
        settings: optimisticSettings,
      }
      pendingSettingsRequestsRef.current.add(requestId)
      mutate(roomKey, optimistic, { revalidate: false })
      setSettingsError(null)
      setIsSavingSettings(true)
      try {
        const next = await transport.updateSettings(token, partial)
        // An older save that lands while a newer one is in flight is still the server's copy.
        if (isSameRoom()) confirmedSettingsRef.current = { roomKey, settings: next.settings }
        if (isCurrent()) mutate(roomKey, next, { revalidate: false })
      } catch {
        if (isCurrent()) {
          const confirmed = confirmedSettingsRef.current?.roomKey === roomKey
            ? confirmedSettingsRef.current.settings
            : previous.settings
          mutate(
            roomKey,
            (data: ChatRoomDto | undefined) => (data ? { ...data, settings: confirmed } : data),
            { revalidate: false },
          )
          setSettingsError(i18n.t('roomPane.saveFailed', { ns: 'app' }))
        }
      } finally {
        pendingSettingsRequestsRef.current.delete(requestId)
        if (isCurrent()) setIsSavingSettings(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUpdateSettings, token, roomData, roomKey, mutate],
  )

  const clearSettingsError = useCallback(() => setSettingsError(null), [])

  const renameRoom = useCallback(
    async (rawTitle: string): Promise<boolean> => {
      const title = rawTitle.trim()
      // Guests never rename; the backend also refuses anyone but the owner.
      if (!token || !title || isGuestTransport) return false
      if (title === roomTitleRef.current) return true
      const session = roomSessionRef.current
      const isCurrent = () => roomSessionRef.current === session
      return runOptimisticRename({
        title,
        previous: roomTitleRef.current,
        getRollbackTitle: () => savedTitleRef.current,
        setTitle: (update) => {
          if (isCurrent()) setRoomTitle(update)
        },
        save: (next) => chatRoomsApi.updateTitle(token, next),
        onFailure: () => {
          toast.error(i18n.t('conversations.renameFailed', { ns: 'app' }))
        },
        onSuccess: (saved) => {
          if (isCurrent()) savedTitleRef.current = saved
          // Patch the cached room too: hydration reads it once on return to this room.
          if (roomKey) void mutate(roomKey, (data: ChatRoomDto | undefined) => (data ? { ...data, title: saved } : data), { revalidate: false })
          patchSidebarRoomTitle(token, saved)
          revalidateConversationLists(mutate, { includeRail: false })
        },
      })
    },
    [token, isGuestTransport, mutate, roomKey],
  )

  // Enqueue a message — always safe to call even while another is in-flight.
  // Also auto-names the room from the first user message (mirrors backend logic).
  const handleSend = useCallback(
    (
      content: string,
      images?: Array<ChatImage>,
      captureUrl?: string,
      stimulusVideos?: Array<{
        url: string
        label?: string
        mimeType?: string
      }>,
      options?: HandleSendOptions,
    ): string | undefined => {
      // Trial cap reached — the composer is replaced by the upgrade notice, but
      // guard here too since secondary entry points (sidebar "ask" chips, home
      // auto-send) also route through handleSend.
      if (atMessageLimitRef.current) return undefined
      if (!blockedQueuedId) setSendError(null)
      const clientRequestId = crypto.randomUUID()
      const id = `queued-${clientRequestId}`
      const tellerPicks = options?.tellerPicks?.length ? options.tellerPicks : undefined
      const documents = options?.documents?.length ? options.documents : undefined
      persistQueue([...queueStateRef.current, {
        id, clientRequestId, content, images, captureUrl, stimulusVideos, deliveryState: 'queued',
        ...(tellerPicks && { tellerPicks }),
        ...(documents && { documents }),
      }])

      setRoomTitle((prev) => {
        if (prev) return prev
        const trimmed = content.trim()
        if (trimmed) {
          return trimmed.length > 60 ? trimmed.slice(0, 57) + '...' : trimmed
        }
        // Image-only first message: mirror the backend placeholder so the header
        // is never blank before the generated title arrives.
        if (images && images.length > 0) return 'Image conversation'
        if (stimulusVideos && stimulusVideos.length > 0) return 'Video conversation'
        return prev
      })
      return clientRequestId
    },
    [blockedQueuedId, persistQueue],
  )

  const retryMessages = useCallback(() => {
    if (!tokenRef.current) return
    const session = roomSessionRef.current
    const acceptedIds = new Set(acceptedLegacyIdsRef.current)
    const trackedIds = genericHistoryIds()
    const historyEpoch = beginHistoryRead(trackedIds)
    let historyReadEnded = false
    const finishHistoryRead = () => {
      if (historyReadEnded) return [] as Array<string>
      historyReadEnded = true
      return endHistoryRead(trackedIds, historyEpoch)
    }
    transportRef.current
      .getMessages(tokenRef.current)
      .then((fresh) => {
        const released = finishHistoryRead()
        if (roomSessionRef.current !== session) return
        recoverQueueFromHistory(fresh)
        setMessagesError(false)
        if (fresh.length > 0) {
          setMessages((prev) => {
            const retained = prev.filter((message) => !acceptedIds.has(message.id))
            const existingIds = new Set(retained.map((m) => m.id))
            const missing = fresh.filter((m) => !existingIds.has(m.id))
            let next = retained
            for (const m of missing) next = mergeIncomingMessage(next, m)
            return orderMessages(next)
          })
          for (const id of acceptedIds) acceptedLegacyIdsRef.current.delete(id)
        }
        // Empty is valid — room may simply have no messages yet
        void consumeLatchedTwinsRef.current(released, session)
      })
      .catch(() => {
        const released = finishHistoryRead()
        if (roomSessionRef.current === session) {
          void consumeLatchedTwinsRef.current(released, session)
          setMessagesError(true)
        }
      })
  }, [recoverQueueFromHistory])

  const forgetDeliveryRecovery = useCallback((id: string, options?: { keepWithheld?: boolean }) => {
    const requestId = queueStateRef.current.find(item => item.id === id)?.clientRequestId
    deliveryRecoveryGenerationRef.current.delete(id)
    genericUncertainIdsRef.current.delete(id)
    if (!options?.keepWithheld) withheldRecoveryIdsRef.current.delete(id)
    twinSignalIdsRef.current.delete(id)
    clearHistoryRead(id)
    if (requestId) socketAcceptedRequestIdsRef.current.delete(requestId)
  }, [])

  const handleQueueEdit = useCallback((id: string, content: string) => {
    // The item on the wire keeps its id: rekeying it would orphan the ack it is waiting for.
    if (isDrainingRef.current && id === queueStateRef.current[0]?.id) return
    const current = queueStateRef.current.find(item => item.id === id)
    // A conflicted id is bound to another payload hash, so resubmitting it cannot succeed.
    const conflict = current?.recoveryReason === 'client_request_conflict'
    const nextRequestId = current && content === current.content && !conflict ? current.clientRequestId : crypto.randomUUID()
    const keepWithheld = !conflict && nextRequestId === current?.clientRequestId && isWithheldRecoveryReason(current?.recoveryReason)
    // A new request id keeps the item's picks: they move with it.
    if (current?.clientRequestId && nextRequestId && nextRequestId !== current.clientRequestId) {
      const previousClientRequestId = current.clientRequestId
      setSendOutcomes(prev => [...prev, { clientRequestId: nextRequestId, outcome: 'rekeyed', previousClientRequestId }])
    }
    forgetDeliveryRecovery(id, { keepWithheld })
    persistQueue(queueStateRef.current.map(item => {
      if (item.id !== id) return item
      const next = { ...item, content, deliveryState: 'queued' as const, clientRequestId: nextRequestId }
      return keepWithheld ? next : withoutRecoveryReason(next)
    }))
    if (id === blockedQueuedId) {
      retryCountRef.current = 0
      blockedQueuedIdRef.current = null
      setBlockedQueuedId(null)
      setSendError(null)
    }
  }, [blockedQueuedId, forgetDeliveryRecovery, persistQueue])

  const handleQueueRetry = useCallback((id: string) => {
    if (id !== blockedQueuedId || id !== messageQueue[0]?.id || isDrainingRef.current) return
    const current = queueStateRef.current.find(item => item.id === id)
    // client_request_conflict means this id already belongs to a different payload.
    // Retrying it is rejected forever; a new id is a new claim. send_recovery_required
    // still retries the same id so the server can finish that claim.
    const rekeyConflict = current?.recoveryReason === 'client_request_conflict'
    forgetDeliveryRecovery(id, { keepWithheld: !rekeyConflict })
    retryCountRef.current = 0
    blockedQueuedIdRef.current = null
    setBlockedQueuedId(null)
    setSendError(null)
    persistQueue(queueStateRef.current.map(item => {
      if (item.id !== id) return item
      if (!rekeyConflict) return { ...item, deliveryState: 'queued' as const }
      return withoutRecoveryReason({ ...item, deliveryState: 'queued', clientRequestId: crypto.randomUUID() })
    }))
  }, [blockedQueuedId, forgetDeliveryRecovery, messageQueue, persistQueue])

  const handleQueueRemove = useCallback((id: string) => {
    forgetDeliveryRecovery(id)
    if (dispatchedItemRef.current?.id === id) dispatchedItemRef.current = null
    const removed = queueStateRef.current.find(item => item.id === id)
    persistQueue(queueStateRef.current.filter(item => item.id !== id))
    recordSendOutcome(removed?.clientRequestId, 'failed')
    if (id === messageQueue[0]?.id && !isSending) {
      retryCountRef.current = 0
      setIsWaitingForTurn(false)
      blockedQueuedIdRef.current = null
      setBlockedQueuedId(null)
      setSendError(null)
    }
  }, [forgetDeliveryRecovery, messageQueue, isSending, persistQueue, recordSendOutcome])

  // Drain the queue one message at a time, sequentially.
  // Uses a ref so the effect body always sees the latest roomData without
  // re-registering on every send (`tokenRef` is declared near the top of the
  // hook, above its first use).
  const roomDataRef = useRef(roomData)
  useEffect(() => {
    roomDataRef.current = roomData
  }, [roomData])

  // The one answer to "can a queued message be sent right now". Both the drain
  // effect below and the queue UI read this, so they cannot disagree.
  const queueBlockedReason: QueueBlockedReason | null = isWaitingForTurn
    ? 'room-busy'
    : turnInProgress
      ? 'twins-answering'
      : null

  useEffect(() => {
    // Don't send while the room is blocked — the backend lock is held and we'd
    // get a 409. The effect re-fires when the block clears (triggered by the
    // WebSocket 'turn-complete' event from the backend). Recovery holds the
    // drain too: until the server's canonical history is reconciled we cannot
    // tell an unsent question from one already accepted.
    if (
      isDrainingRef.current ||
      messageQueue.length === 0 ||
      queueBlockedReason !== null ||
      queueRecoveryPending
    )
      return

    const next = messageQueue[0]
    if (next.id === blockedQueuedId || (next.deliveryState && next.deliveryState !== 'queued')) return
    const currentToken = tokenRef.current
    const currentRoom = roomDataRef.current
    if (!currentToken || !currentRoom) return
    if (retryItemIdRef.current !== next.id) {
      retryCountRef.current = 0
      retryItemIdRef.current = next.id
    }
    const session = roomSessionRef.current
    const currentTransport = transportRef.current
    const dispatchedStorageKey = queueStorageKeyRef.current
    const dispatchedRoomKey = roomKeyRef.current
    const isGuestDispatch = currentTransport.scope !== authenticatedChatRoomTransport.scope
    // The server lists a room once it holds a user message, so the first
    // accepted send is when the room joins every conversation list.
    const isFirstUserSend = !messagesRef.current.some(
      (message) => message.senderType === 'user' && !message.id.startsWith('optimistic-'),
    )

    isDrainingRef.current = true
    sendRevisionRef.current += 1
    awaitingTurnIdentityRef.current = true
    setSendError(null)
    persistQueue(queueStateRef.current.map(item => {
      if (item.id !== next.id) return item
      if (isWithheldRecoveryReason(item.recoveryReason)) return { ...item, deliveryState: 'uncertain' as const }
      return { ...item, deliveryState: 'uncertain' as const, recoveryReason: 'generic' as const }
    }), dispatchedStorageKey)
    dispatchedItemRef.current = queueStateRef.current.find(item => item.id === next.id) ?? null

    const optimisticMsg: ChatRoomMessageDto = {
      id: `optimistic-${next.id}`,
      roomId: currentRoom.token,
      sequenceIndex: -1,
      senderParticipantId: null,
      senderType: 'user',
      content: next.content,
      mode: 'chat',
      metadataType: null,
      // Lets the thread keep this bubble's element when the canonical copy replaces it.
      metadata: { pendingClientRequestId: next.clientRequestId },
      ...(next.images && next.images.length > 0 && { images: next.images }),
      ...(next.stimulusVideos &&
        next.stimulusVideos.length > 0 && {
          videoRefs: next.stimulusVideos.map((video) => ({ kind: 'video' as const, ...video })),
        }),
      // The document chip shows at once; the stored key arrives with the ack.
      ...(next.documents &&
        next.documents.length > 0 && {
          documentRefs: next.documents.map((document) => ({ key: '', name: document.name, mimeType: document.mimeType })),
        }),
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, optimisticMsg])
    setIsSending(true)
    // A fast turn may complete before the HTTP acknowledgement arrives.
    setTurnInProgress(true)

    currentTransport
      .sendMessage(currentToken, {
        content: next.content,
        clientRequestId: next.clientRequestId,
        mode: 'chat',
        ...(next.images && next.images.length > 0 && { images: next.images }),
        ...(next.captureUrl && next.captureUrl.trim() && { captureUrl: next.captureUrl.trim() }),
        ...(next.stimulusVideos &&
          next.stimulusVideos.length > 0 && {
            stimulusVideos: next.stimulusVideos,
          }),
        ...(next.tellerPicks && next.tellerPicks.length > 0 && { tellerPicks: next.tellerPicks }),
        ...(next.documents && next.documents.length > 0 && { documents: next.documents }),
      })
      .then(async (ack) => {
        if (dispatchedItemRef.current?.id === next.id) dispatchedItemRef.current = null
        if (provisionalImageCarryRef.current?.requestId === next.clientRequestId) provisionalImageCarryRef.current = null
        removeAcceptedChatQueueItem(dispatchedStorageKey, next.clientRequestId)
        if (!isGuestDispatch) {
          // Every accepted send moves the room to the top of the rail (cache
          // patch, no request); the first one also refreshes the other lists.
          // Runs before the session check: the room started even if the user
          // has already moved on to another one.
          // Best effort: a cache hiccup must never turn an accepted send into
          // a failed one (the catch below runs send recovery).
          try {
            const isSameSession = roomSessionRef.current === session
            upsertSidebarRoom(
              recentRoomItemFromRoom(
                currentRoom,
                isSameSession ? roomTitleRef.current ?? currentRoom.title : currentRoom.title,
                ack?.message?.createdAt ?? new Date().toISOString(),
              ),
            )
            if (isFirstUserSend) {
              revalidateConversationLists(mutate)
            }
          } catch {
            // The lists catch up on their next revalidation.
          }
        }
        if (roomSessionRef.current !== session) return
        retryCountRef.current = 0
        if (ack?.message) {
          const accepted = ack.message
          observeUserMessage(accepted, true)
          setMessages((prev) => orderMessages(mergeIncomingMessage(prev, accepted, optimisticMsg.id)))
        } else {
          // Compatibility with an older server during rollout: acceptance
          // guarantees persistence, so read canonical history without guessing
          // which identical-text message belongs to this send.
          acceptedLegacyIdsRef.current.add(optimisticMsg.id)
          const acceptedIds = new Set(acceptedLegacyIdsRef.current)
          try {
            const fresh = await currentTransport.getMessages(currentToken)
            if (roomSessionRef.current !== session) return
            if (fresh.length === 0) throw new Error('Accepted message is not visible yet')
            setMessages((prev) =>
              orderMessages(fresh.reduce(
                  (messages, message) => mergeIncomingMessage(messages, message),
                  prev.filter((message) => !acceptedIds.has(message.id)),
                )),
            )
            for (const id of acceptedIds) acceptedLegacyIdsRef.current.delete(id)
          } catch {
            if (roomSessionRef.current !== session) return
            setMessagesError(true)
          }
        }
        persistQueue(queueStateRef.current.filter(item => item.id !== next.id))
        setLastAcceptedClientRequestId(next.clientRequestId ?? null)
        recordSendOutcome(next.clientRequestId, 'accepted')
        // Picks can raise the teller count or activate an off twin server side.
        if (next.tellerPicks && next.tellerPicks.length > 0 && dispatchedRoomKey) {
          void mutate(dispatchedRoomKey)
        }
        setIsSending(false)
      })
      .catch(async (err) => {
        if (roomSessionRef.current !== session) return
        const dispatched = dispatchedItemRef.current?.id === next.id
          && dispatchedItemRef.current.clientRequestId === next.clientRequestId
          ? dispatchedItemRef.current
          : null
        if (dispatched) dispatchedItemRef.current = null

        const rejectLocally = () => {
          awaitingTurnIdentityRef.current = false
          setTurnInProgress(false)
          setMessages((prev) => prev.filter((m) => m.id !== optimisticMsg.id))
        }

        if (err?.response?.data?.code === 'send_recovery_required' || err?.response?.data?.code === 'client_request_conflict') {
          const carried = provisionalImageCarryRef.current
          if (carried && carried.requestId === next.clientRequestId) {
            provisionalImageCarryRef.current = null
            setMessages(prev => prev.map(message => {
              if (message.id !== carried.messageId || !chatImagesMatch(message.images, carried.images)) return message
              const restored = { ...message }
              delete restored.images
              return restored
            }))
          }
          rejectLocally()
          const recoveryReason = err.response.data.code
          // The reused id belongs to an earlier payload. History may already
          // have dropped this in-flight item; put that payload back withheld.
          const stillQueued = queueStateRef.current.some(item => item.id === next.id && item.clientRequestId === next.clientRequestId)
          const source = stillQueued
            ? queueStateRef.current.find(item => item.id === next.id)
            : dispatched
          if (!source) {
            setIsSending(false)
            return
          }
          const withheldItem: QueuedMessage = { ...source, deliveryState: 'uncertain', recoveryReason }
          withheldRecoveryIdsRef.current.add(next.id)
          genericUncertainIdsRef.current.delete(next.id)
          twinSignalIdsRef.current.delete(next.id)
          deliveryRecoveryGenerationRef.current.delete(next.id)
          if (next.clientRequestId) socketAcceptedRequestIdsRef.current.delete(next.clientRequestId)
          persistQueue(stillQueued
            ? queueStateRef.current.map(item => item.id === next.id ? withheldItem : item)
            : [withheldItem, ...queueStateRef.current])
          setIsSending(false)
          blockedQueuedIdRef.current = next.id
          setBlockedQueuedId(next.id)
          setSendError(err.response.data.message || 'This send needs recovery. Refresh the conversation before retrying.')
          return
        }
        if (err?.response?.status === 409) {
          rejectLocally()
          persistQueue(queueStateRef.current.map(item => item.id === next.id ? withoutRecoveryReason({ ...item, deliveryState: 'queued' }) : item))
          setIsSending(false)
          setIsWaitingForTurn(true)
          return
        }
        if (detectTrialLimitError(err) === 'messages') {
          // Trial message cap — swap the composer for the upgrade notice rather
          // than showing a generic send error.
          rejectLocally()
          setIsSending(false)
          setServerMessageLimitHit(true)
          persistQueue(queueStateRef.current.filter(item => item.id !== next.id))
          recordSendOutcome(next.clientRequestId, 'failed')
          return
        }

        // The HTTP call failed after the body may already have been accepted.
        // Reconcile by clientRequestId before showing a sticky delivery banner.
        const requestId = next.clientRequestId
        const stopSending = () => {
          if (roomSessionRef.current === session) setIsSending(false)
        }
        const queuedItem = () => queueStateRef.current.find(queued =>
          queued.id === next.id && queued.clientRequestId === requestId && queued.deliveryState !== 'queued',
        )
        if (withheldRecoveryIdsRef.current.has(next.id)) {
          // This id was already rejected as a different payload. A later generic
          // failure must not treat the earlier transcript row as acceptance.
          rejectLocally()
          stopSending()
          blockedQueuedIdRef.current = next.id
          setBlockedQueuedId(next.id)
          setSendError(UNCONFIRMED_DELIVERY_ERROR)
          return
        }
        if (!queuedItem()) {
          if (provisionalImageCarryRef.current?.requestId === requestId) provisionalImageCarryRef.current = null
          if (requestId && acceptedRequestIds(messagesRef.current).has(requestId)) {
            setMessages(prev => {
              const canonical = prev.find(message => clientRequestIdOf(message) === requestId)
              if (!canonical) return prev.filter(message => message.id !== optimisticMsg.id)
              return orderMessages(mergeIncomingMessage(prev, canonical, optimisticMsg.id))
            })
          }
          // History already took this send off the queue while the HTTP call was
          // still in flight. Release the drain after that state update, or a
          // turn that finished in the meantime never sends the next item.
          if (roomSessionRef.current === session) {
            isDrainingRef.current = false
            setIsSending(false)
            setMessageQueue(queueStateRef.current.slice())
          }
          return
        }
        const generation = (deliveryRecoveryGenerationRef.current.get(next.id) ?? 0) + 1
        deliveryRecoveryGenerationRef.current.set(next.id, generation)
        genericUncertainIdsRef.current.add(next.id)
        const historyEpoch = beginHistoryRead([next.id])
        persistQueue(queueStateRef.current.map(item => item.id === next.id
          ? { ...item, deliveryState: 'uncertain' as const, recoveryReason: 'generic' as const }
          : item))
        const stillCurrent = () => {
          if (roomSessionRef.current !== session) return false
          if (deliveryRecoveryGenerationRef.current.get(next.id) !== generation) return false
          return Boolean(queuedItem())
        }
        const provedLocally = () => Boolean(
          requestId && (
            socketAcceptedRequestIdsRef.current.has(requestId) ||
            acceptedRequestIds(messagesRef.current).has(requestId)
          ),
        )
        const finishAccepted = (fresh: Array<ChatRoomMessageDto>) => {
          if (!stillCurrent()) return
          const item = queuedItem()
          if (item) confirmAcceptedRequestRef.current(item, fresh)
        }

        try {
          let attempt = 0
          while (attempt < 3) {
            attempt += 1
            if (attempt > 1) {
              if (!twinSignalIdsRef.current.has(next.id)) break
              twinSignalIdsRef.current.delete(next.id)
            }
            if (!stillCurrent()) {
              stopSending()
              return
            }
            if (provedLocally()) {
              finishAccepted(messagesRef.current)
              stopSending()
              return
            }
            if (!requestId) break
            let fresh: Array<ChatRoomMessageDto> | null = null
            try {
              fresh = await currentTransport.getMessages(currentToken)
            } catch {
              fresh = null
            }
            if (!stillCurrent()) {
              stopSending()
              return
            }
            if (fresh && acceptedRequestIds(fresh).has(requestId)) {
              finishAccepted(fresh)
              stopSending()
              return
            }
            if (provedLocally()) {
              finishAccepted(messagesRef.current)
              stopSending()
              return
            }
            if (!twinSignalIdsRef.current.has(next.id)) break
          }
        } finally {
          endHistoryRead([next.id], historyEpoch)
        }
        if (!stillCurrent()) {
          stopSending()
          return
        }
        rejectLocally()
        setIsSending(false)
        blockedQueuedIdRef.current = next.id
        setBlockedQueuedId(next.id)
        setSendError(UNCONFIRMED_DELIVERY_ERROR)
      })
      .finally(() => {
        if (roomSessionRef.current === session) isDrainingRef.current = false
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageQueue, queueBlockedReason, blockedQueuedId, queueRecoveryPending])

  // When a 409 is received: retry with exponential back-off.
  useEffect(() => {
    if (!isWaitingForTurn || messageQueue.length === 0) return

    const MAX_RETRIES = 5
    if (retryCountRef.current >= MAX_RETRIES) {
      persistQueue(queueStateRef.current.map(item => item.id === messageQueue[0].id ? { ...item, deliveryState: 'blocked' } : item))
      blockedQueuedIdRef.current = messageQueue[0].id
      setBlockedQueuedId(messageQueue[0].id)
      setIsWaitingForTurn(false)
      setIsSending(false)
      setSendError('The room is still busy. Retry this message, edit it, or remove it.')
      return
    }

    const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 15_000)
    const timer = setTimeout(() => {
      retryCountRef.current += 1
      setIsWaitingForTurn(false)
      setIsSending(false)
      isDrainingRef.current = false
      setTurnInProgress(false)
      setMessageQueue((prev) => [...prev])
    }, delay)
    return () => clearTimeout(timer)
  }, [isWaitingForTurn, messageQueue, persistQueue])

  // Safety-net: if the 'turn-complete' WebSocket event is missed (multi-pod,
  // socket disconnect), release the drain gate after 30s.
  useEffect(() => {
    if (!turnInProgress) return
    const fallback = setTimeout(() => {
      setTurnInProgress(false)
    }, 30_000)
    return () => clearTimeout(fallback)
  }, [turnInProgress])

  return {
    roomData,
    roomLoading,
    roomError,
    participants,
    roomStatus,
    roomTitle,
    messages,
    messagesLoading,
    participantStates,
    thinkingParticipantIds,
    isSending,
    sendError: [sendError, queuePersistenceError].filter(Boolean).join(' ') || null,
    isWaitingForTurn,
    queueBlockedReason,
    isClosed,
    isPreparing,
    isError,
    atMessageLimit,
    messageQueue,
    blockedQueuedId,
    handleQueueRetry,
    handleSend,
    handleQueueEdit,
    handleQueueRemove,
    handleSettingsChange,
    renameRoom,
    canUpdateSettings,
    isSavingSettings,
    settingsError,
    clearSettingsError,
    navigate,
    messagesError,
    retryMessages,
    lastAcceptedClientRequestId,
    sendOutcomes,
    reactions,
    liveReactionKeys,
    openingQuestion: isGuestTransport || messages.length > 0 ? null : openingQuestion,
  }
}
